import { ChartsData } from "@aura-frontend/omnibus-az-client";
import { useMemo } from "react";

import { useChartData, UseChartDataParams } from "@/api/useChartData";
import {
  CHART_HEIGHT_PX_FEET_RATIO,
  CHART_ORIGIN_X,
  CHART_ORIGIN_Y,
} from "@/assets/SprayChartVisualization";
import { COLORS } from "@/config/colors";
import { deg2Rad } from "@/lib/utils";

import { PitchTypeColors } from "../TiltAndSpinChartCard/TiltAndSpinChartDataCard";
import { SprayChartCard, SprayChartData } from ".";

export interface SprayChartDataCardProps extends Omit<
  UseChartDataParams,
  "groupBy" | "chartType"
> {
  /** The title to display on the scatter chart card */
  title: string;
  pitchType?: string;
  maxSpeed?: number;
  showLegend?: boolean;
  hitsAndOutsOnly?: boolean;
}

/**
 * Transforms raw spray chart data into a structured chart dataset.
 *
 * @param rawDataset - The raw data object containing spray chart points and metadata.
 * @returns An object with label, color, points (with x/y coordinates), and icon type,
 *          or an empty array if no points are present.
 */
export const transformSprayChartData = (
  rawDataset: unknown,
): SprayChartData => {
  const dataset = rawDataset as ChartsData;

  // Extract and transform the coordinates from polar (distance, angle) to cartesian (x, y)
  const points: { x: number; y: number }[] = Array.isArray(
    dataset.points_spray_chart,
  )
    ? dataset.points_spray_chart.map((coords) => {
        const distance = (coords as number[])[0];
        const angle = (coords as number[])[1];

        // Convert angle to radians and adjust so 0º is up
        const normalizedAngle = deg2Rad(angle - 90);
        const normalizedDistance = distance * CHART_HEIGHT_PX_FEET_RATIO;

        return {
          x: CHART_ORIGIN_X + Math.cos(normalizedAngle) * normalizedDistance,
          y: CHART_ORIGIN_Y + Math.sin(normalizedAngle) * normalizedDistance,
        };
      })
    : [];

  return {
    label: `${"is_out" in dataset && dataset.is_out ? "Out" : "Hits"} vs ${dataset.pitch_type || "Other"}`,
    color: PitchTypeColors[dataset.pitch_type || "Other"] || COLORS.black,
    points,
    icon: "is_out" in dataset && dataset.is_out ? "triangle" : "circle",
  };
};

const sortByLabels = (a: ChartsData, b: ChartsData): number => {
  if (!a.pitch_type || !b.pitch_type) {
    return 0;
  }
  const localeSort = a.pitch_type.localeCompare(b.pitch_type);
  if (localeSort === 0) {
    return "is_hit" in a && a.is_hit ? 1 : -1;
  }

  return localeSort;
};

export const SprayChartDataCard: React.FC<SprayChartDataCardProps> = ({
  title,
  playerExternalId,
  startDate,
  endDate,
  gameType,
  playerType,
  vsHandedness,
  filters,
  showLegend = true,
  hitsAndOutsOnly = true,
}) => {
  const { data, isLoading, error } = useChartData({
    playerExternalId,
    startDate,
    endDate,
    gameType,
    playerType,
    vsHandedness,
    chartType: "spray_chart",
    filters,
    hitsAndOutsOnly,
    ...(showLegend && { groupBy: ["is_out", "is_hit", "pitch_type"] }),
  });

  const datasets: SprayChartData[] = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data
      .filter(
        (rawDataset: ChartsData) =>
          "points_spray_chart" in rawDataset &&
          Array.isArray(rawDataset.points_spray_chart) &&
          rawDataset.points_spray_chart.length > 0,
      )
      .sort(sortByLabels)
      .map(transformSprayChartData);
  }, [data]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg min-h-[330px]">
        <p className="text-sm text-gray-500">Unable to load chart data</p>
      </div>
    );
  }

  return (
    <SprayChartCard
      title={title}
      loading={isLoading}
      data={datasets}
      showLegend={showLegend}
    />
  );
};
